import { useState } from 'react';
import type { PublicEvent } from '@arenapulse/shared';

interface EventCardProps {
  event: PublicEvent;
  onDeleteClick: (event: PublicEvent) => void;
  onOpenArena: (event: PublicEvent) => void;
}

export function EventCard({ event, onDeleteClick, onOpenArena }: EventCardProps) {
  const [isCopied, setIsCopied] = useState(false);

  const handleShareClick = async () => {
    const shareUrl = `${import.meta.env.VITE_WEBSITE_URL}/join?code=${event.join_code}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.name,
          url: shareUrl,
        });
      } catch (error) {
        // Fallback to clipboard if share fails (e.g. user cancelled, or not supported on device)
        copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      prompt('Copy the link below:', text);
    }
  };

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-border p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{event.name}</h3>
        {event.description && (
          <p className="text-sm text-muted mt-1">{event.description}</p>
        )}
        <div className="flex items-center gap-4 mt-4">
          <span className="inline-flex items-center rounded-md bg-border px-2 py-1 text-xs font-medium text-foreground ring-1 ring-inset ring-border uppercase">
            {event.status}
          </span>
          <div className="text-sm">
            <span className="text-muted">Join Code: </span>
            <span className="font-mono font-medium text-foreground">{event.join_code}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={handleShareClick}
          className="inline-flex items-center rounded-md bg-transparent px-3 py-2 text-sm font-semibold text-foreground shadow-sm hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary border border-border"
        >
          {isCopied ? 'Copied!' : 'Share'}
        </button>
        <button
          onClick={() => onOpenArena(event)}
          className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          Open Arena
        </button>
        <button
          onClick={() => onDeleteClick(event)}
          className="text-sm font-medium text-destructive hover:text-destructive transition-colors focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2 rounded"
        >
          Delete
        </button>
      </div>
    </div>
  );
}
